"use client";

import { useEffect, useRef } from "react";

import { getDemoEmailContent } from "./email-actions.server";
import { useEmailPreview, type EmailData } from "./email-preview-modal";
import { useNotifications, type Notification } from "./notifications";

type QueuedNotification = Omit<Notification, "id" | "timestamp" | "action">;

interface NotificationQueue {
  notifications: QueuedNotification[];
  timestamp: number;
}

const COOKIE_NAME = "demo_notification_queue";

function parseCookies(): Record<string, string> {
  if (typeof document === "undefined") return {};
  const cookies: Record<string, string> = {};
  document.cookie.split(";").forEach((cookie) => {
    const [name, value] = cookie.trim().split("=");
    if (name && value) {
      cookies[name] = value;
    }
  });
  return cookies;
}

function clearNotificationCookie() {
  if (typeof document === "undefined") return;
  document.cookie = `${COOKIE_NAME}=; path=/; max-age=0`;
}

function getQueuedNotifications(): QueuedNotification[] {
  const cookies = parseCookies();
  const queueData = cookies[COOKIE_NAME];

  if (!queueData) return [];

  try {
    const decoded = decodeURIComponent(queueData);
    const queue: NotificationQueue = JSON.parse(decoded);
    return queue.notifications || [];
  } catch {
    return [];
  }
}

/**
 * Hook that watches for demo notifications queued by server actions.
 * Place this in a component that wraps the app (e.g., DemoModeProvider).
 */
export function useDemoNotificationQueue() {
  const { showNotification } = useNotifications();
  const { openEmailPreview } = useEmailPreview();
  const lastProcessedRef = useRef<number>(0);
  const showNotificationRef = useRef(showNotification);
  const openEmailPreviewRef = useRef(openEmailPreview);

  // Keep refs updated
  useEffect(() => {
    showNotificationRef.current = showNotification;
    openEmailPreviewRef.current = openEmailPreview;
  }, [showNotification, openEmailPreview]);

  useEffect(() => {
    const timeoutIds: ReturnType<typeof setTimeout>[] = [];

    // Check for notifications on mount and periodically
    const checkNotifications = () => {
      const cookies = parseCookies();
      const queueData = cookies[COOKIE_NAME];

      if (!queueData) return;

      try {
        const decoded = decodeURIComponent(queueData);
        const queue: NotificationQueue = JSON.parse(decoded);

        // Only process if this is a new batch
        if (queue.timestamp <= lastProcessedRef.current) {
          return;
        }

        lastProcessedRef.current = queue.timestamp;

        // Show notifications with staggered delays
        queue.notifications.forEach((notification, index) => {
          const timeoutId = setTimeout(
            () => {
              // If notification has emailData, add an "Open" action
              if (notification.emailData) {
                const emailData = notification.emailData;
                showNotificationRef.current({
                  ...notification,
                  action: {
                    label: "Open",
                    onClick: async () => {
                      // Fetch the email content from server
                      const emailContent = await getDemoEmailContent(
                        emailData.emailKey
                      );
                      if (emailContent) {
                        const email: EmailData = {
                          from: emailData.from,
                          to: emailData.to,
                          subject: emailData.subject,
                          date: new Date(),
                          htmlContent: emailContent.htmlContent,
                        };
                        openEmailPreviewRef.current(email);
                      }
                    },
                  },
                });
              } else {
                showNotificationRef.current(notification);
              }
            },
            index * 500 + 100
          ); // 500ms between each notification, 100ms initial delay
          timeoutIds.push(timeoutId);
        });

        // Clear the cookie after processing
        clearNotificationCookie();
      } catch {
        // Invalid data, clear it
        clearNotificationCookie();
      }
    };

    // Check immediately
    checkNotifications();

    // Poll every 500ms for new notifications
    const interval = setInterval(checkNotifications, 500);

    return () => {
      clearInterval(interval);
      timeoutIds.forEach((id) => clearTimeout(id));
    };
  }, []); // Remove showNotification dependency to prevent re-running
}

/**
 * Get the notifications that are currently queued (for debugging)
 */
export function getQueuedDemoNotifications(): QueuedNotification[] {
  return getQueuedNotifications();
}
